import type { PipelineScopeNode, PipelineScopeReport } from '@/entities/expert-report';
import type { ExpertReportPipelineScope } from '@/entities/search';

/**
 * Пункт каскадного меню массовых действий: подменю задаётся вложенными пунктами.
 * Вложенность любой глубины — дерево узлов заключения раскрывается до конечных блоков.
 */
export type PipelineMenuItem = {
  type: 'item' | 'label';
  key: string;
  title: string;
  onClick?: () => void;
  children?: PipelineMenuItem[];
};

/** Подписи запуска уровня целиком: заголовки первых пунктов подменю каждого уровня */
export type PipelineScopeLabels = {
  /** Запуск по всем заключениям — область не задаётся */
  allReports: string;
  /** Запуск по всему заключению */
  wholeReport: string;
  /** Запуск по всей группе узлов */
  wholeGroup: string;
};

/** Выбор области запуска; без области — генерация по всем применимым заключениям целиком */
type SelectScope = (scope?: ExpertReportPipelineScope) => void;

/**
 * Пункт узла структуры заключения. Конечный узел запускается своим пунктом, узел с вложенными
 * раскрывает подменю: пункт с подменю выбором не является, поэтому запуск по узлу целиком стоит
 * первым пунктом внутри него.
 */
const buildNodeItem = (
  node: PipelineScopeNode,
  expertReportId: number,
  labels: PipelineScopeLabels,
  onSelect: SelectScope,
): PipelineMenuItem => {
  const key = `report-${expertReportId}-node-${node.id}`;
  const runNode = () => onSelect({ expertReportId, blockId: node.id });

  if (node.children.length === 0) {
    return { type: 'item', key, title: node.name, onClick: runNode };
  }

  return {
    type: 'item',
    key,
    title: node.name,
    children: [
      { type: 'item', key: `${key}-whole`, title: labels.wholeGroup, onClick: runNode },
      ...node.children.map((child) => buildNodeItem(child, expertReportId, labels, onSelect)),
    ],
  };
};

/** Пункт заключения: без узлов запускается сразу, с узлами — раскрывает их подменю */
const buildReportItem = (
  report: PipelineScopeReport,
  labels: PipelineScopeLabels,
  onSelect: SelectScope,
): PipelineMenuItem => {
  const key = `report-${report.id}`;
  const runReport = () => onSelect({ expertReportId: report.id });

  if (report.nodes.length === 0) {
    return { type: 'item', key, title: report.name, onClick: runReport };
  }

  return {
    type: 'item',
    key,
    title: report.name,
    children: [
      { type: 'item', key: `${key}-whole`, title: labels.wholeReport, onClick: runReport },
      ...report.nodes.map((node) => buildNodeItem(node, report.id, labels, onSelect)),
    ],
  };
};

/**
 * Пункты выбора области запуска генерации экспертных заключений: первый уровень — сами
 * заключения, дальше на каждом шаге — узлы наведённого узла, до конечных блоков.
 */
export const buildPipelineScopeItems = (
  reports: PipelineScopeReport[],
  labels: PipelineScopeLabels,
  onSelect: SelectScope,
): PipelineMenuItem[] => [
  { type: 'item', key: 'all-reports', title: labels.allReports, onClick: () => onSelect() },
  ...reports.map((report) => buildReportItem(report, labels, onSelect)),
];
